import { Banner, TabsWithContent } from "@homebound/beam";
import { useEffect, useState } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  PageBidPackageDetailFragment,
  useBidPackageDetailPageQuery,
  useBidPackageVersionDetailPageQuery,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { BillTermsTabView } from "src/routes/bids/components/BillTermsTab";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { BidPackageParams } from "../routesDef";
import { BidPackageHeader } from "./components/BidPackageHeader";
import { BiddersTab } from "./components/BiddersTab";
import { BidsTab } from "./components/BidsTab";
import { OverlappingBidPackagesTab } from "./components/OverlappingBidPackagesTab";

export function BidPackageDetailPage() {
  const { bidPackageId } = useRouteParams<BidPackageParams>();
  const [{ versionId }] = useQueryParams({ versionId: StringParam });
  const query = useBidPackageDetailPageQuery({ variables: { id: bidPackageId }, skip: !!versionId });
  const versionQuery = useBidPackageVersionDetailPageQuery({ variables: { id: versionId! }, skip: !versionId });
  if (versionId) {
    return queryResult(versionQuery, ({ bidPackageVersion }) => (
      <BidPackageDetailView
        bidPackage={{
          ...bidPackageVersion,
          ...bidPackageVersion.bidPackage,
          status: bidPackageVersion.status,
          latestVersion: bidPackageVersion,
          // We don't want users accepting updates while viewing old versions
          availableUpdates: [],
          __typename: "BidPackage",
        }}
        displayingOldVersion
      />
    ));
  }
  return queryResult(query, ({ bidPackage }) => <BidPackageDetailView bidPackage={bidPackage} />);
}

type TabValue = "bids" | "bidders" | "billTerms" | "overlappingBidPackages";

type BidPackageDetailViewProps = { bidPackage: PageBidPackageDetailFragment; displayingOldVersion?: boolean };

export function BidPackageDetailView({ bidPackage, displayingOldVersion = false }: BidPackageDetailViewProps) {
  const { bidPackageId } = useRouteParams<BidPackageParams>();
  const [selectedTab, setSelectedTab] = useState<TabValue>("bids");
  const tradePartnerRevisions = bidPackage.activeRequests.map((request) => ({
    tradePartner: request.tradePartner,
    revision: bidPackage.latestVersion.latestBidContractRevisions.find(
      (bcr) => bcr.bidContract.tradePartner?.id === request.tradePartner.id,
    ),
  }));
  const tradesWithInvalidDrawTasks = tradePartnerRevisions
    .filter((tpr) => tpr.revision?.hasValidDrawTasks === false)
    .map((tpr) => tpr.tradePartner.name);
  const [showDrawTaskError, setShowDrawTaskError] = useState(tradesWithInvalidDrawTasks.nonEmpty);

  // When the bid package ID changes, re-default the selected tab
  useEffect(() => setSelectedTab("bids"), [bidPackageId]);

  return (
    <>
      <BidPackageHeader bidPackage={bidPackage} displayingOldVersion={displayingOldVersion} />
      {showDrawTaskError && (
        <Banner
          type="error"
          message={`One or more bill terms for ${tradesWithInvalidDrawTasks.join(", ")} has a labor task not included on the version. Amend the bill terms to award.`}
          onClose={() => setShowDrawTaskError(false)}
        />
      )}
      <TabsWithContent
        alwaysShowAllTabs
        tabs={[
          { name: "Bids", value: "bids", render: () => <BidsTab bidPackage={bidPackage} /> },
          {
            name: "Bidders",
            value: "bidders",
            render: () => <BiddersTab bidPackage={bidPackage} />,
            disabled: displayingOldVersion || bidPackage.isInternalEstimate,
          },
          {
            name: "Bill Terms",
            value: "billTerms",
            render: () => (
              <BillTermsTabView
                bidPackageId={bidPackage.id}
                entityId={bidPackage.projects.first?.id!}
                tradePartnerRevisions={tradePartnerRevisions}
              />
            ),
            disabled: displayingOldVersion || disableBasedOnPotentialOperation(bidPackage.latestVersion.canHaveDraws),
          },
          {
            name: "Overlap",
            value: "overlappingBidPackages",
            disabled: bidPackage.overlappingBidPackages.isEmpty
              ? "This bid package doesn't have any overlapping bid packages."
              : displayingOldVersion,
            render: () => <OverlappingBidPackagesTab bidPackage={bidPackage} />,
          },
        ]}
        selected={displayingOldVersion ? "bids" : selectedTab}
        onChange={setSelectedTab}
      />
    </>
  );
}
